/*
 * Project: bellcloud
 * File: apps/frontend/src/connections/asyncQueue.ts
 * Description: Serial/BLE/시뮬 어댑터 공용 비동기 큐.
 *   데이터가 들어오면 대기 중인 shift()를 즉시 해제한다.
 */

interface Waiter<T> {
  resolve: (value: T) => void;
  reject: (error: Error) => void;
  timeoutId: ReturnType<typeof setTimeout> | null;
}

export class AsyncQueue<T> {
  private items: T[] = [];
  private waiters: Array<Waiter<T>> = [];

  get size(): number {
    return this.items.length;
  }

  /** 버퍼된 아이템만 비운다. 대기 중인 shift()는 그대로 둔다. */
  clear(): void {
    this.items = [];
  }

  /** 대기 중인 모든 shift()를 reject하고 큐를 비운다 (WS 오류 등). */
  rejectAll(error: Error): void {
    for (const w of this.waiters) {
      if (w.timeoutId) clearTimeout(w.timeoutId);
      w.reject(error);
    }
    this.waiters = [];
    this.items = [];
  }

  push(item: T): void {
    const waiter = this.waiters.shift();
    if (waiter) {
      if (waiter.timeoutId) clearTimeout(waiter.timeoutId);
      waiter.resolve(item);
      return;
    }
    this.items.push(item);
  }

  shift(timeoutMs: number): Promise<T> {
    const item = this.items.shift();
    if (item !== undefined) {
      return Promise.resolve(item);
    }

    return new Promise<T>((resolve, reject) => {
      const waiter: Waiter<T> = { resolve, reject, timeoutId: null };

      if (timeoutMs > 0) {
        waiter.timeoutId = setTimeout(() => {
          const idx = this.waiters.indexOf(waiter);
          if (idx >= 0) this.waiters.splice(idx, 1);
          reject(new Error(`Timeout: No stream packet received within ${timeoutMs}ms`));
        }, timeoutMs);
      }

      this.waiters.push(waiter);
    });
  }
}
